import { useEffect, useRef } from "react";
import { useToast } from "../components/Toast";

// The toast is a single nowrap line, so a multi-branch conflict list (joined
// with newlines by the pull) would stretch it past the viewport. The sync
// button's tooltip carries the message in full.
const MAX_TOAST_ERROR = 120;

export function toastMessage(error: string): string {
  const oneLine = error.replace(/\s+/g, " ").trim();
  const gist =
    oneLine.length > MAX_TOAST_ERROR
      ? `${oneLine.slice(0, MAX_TOAST_ERROR - 1).trimEnd()}…`
      : oneLine;
  return `Git sync failed: ${gist}`;
}

/**
 * Whether a sync just ended badly, and whether to keep watching. A sync reports
 * its outcome long after the click by writing the space's remote row, so the
 * mutation resolving says nothing about whether it worked and the failure has to
 * be caught on the transition out of syncing. Only a run seen starting counts,
 * which keeps a failure from a previous session from announcing itself when a
 * page that was never watching one happens to render.
 */
export function nextSyncAnnouncement(
  watching: boolean,
  isSyncing: boolean,
  syncError: string | null
): { watching: boolean; announce: string | null } {
  if (isSyncing) return { watching: true, announce: null };
  if (!watching) return { watching: false, announce: null };
  return { watching: false, announce: syncError };
}

export function useGitSyncErrorToast(
  isSyncing: boolean,
  syncError: string | null
) {
  const { showToast } = useToast();
  const watching = useRef(false);
  useEffect(() => {
    const next = nextSyncAnnouncement(watching.current, isSyncing, syncError);
    watching.current = next.watching;
    if (next.announce === null) return;
    showToast({
      icon: "circle-off",
      message: toastMessage(next.announce),
      buttonLabel: "Dismiss",
      variant: "error",
    });
  }, [isSyncing, syncError, showToast]);
}
